import { type Edge, type Node } from '@xyflow/react';
import { layoutFlowGraph } from './workflow-graph-to-flow';
import {
  WORKFLOW_FLOW_NODE_HEIGHT,
  WORKFLOW_FLOW_NODE_WIDTH,
} from './workflow-flow-layout.constants';
import {
  WORKFLOW_RUN_FLOW_NODE_TYPE,
  type WorkflowRunFlowNodeData,
} from './workflow-run-flow-node';
import type { WorkflowNodeViewData } from './components/node';
import { asRecord, readString } from './components/utils';
import { expandRunGraphForFanOut } from './workflow-run-graph-fan-out';
import type {
  WorkflowRunGraphEdge,
  WorkflowRunGraphNode,
} from './workflow-run.types';
import { WORKFLOW_EDGE_TYPE } from './workflow-edge-style';

export type WorkflowRunFlowNode = Node<WorkflowRunFlowNodeData>;

/**
 * Ensures strategies that read `data.name` (run-input, agent, tool-call) see the
 * API display `name` when the persisted node payload omitted it.
 */
function mergeDisplayName(data: unknown, name: string): unknown {
  if (name === '') {
    return data;
  }
  const record = asRecord(data);
  if (record === null) {
    return { name };
  }
  const existing = readString(record, 'name');
  if (existing !== undefined && existing !== '') {
    return data;
  }
  return { ...record, name };
}

function withForcedDisplayName(data: unknown, name: string): unknown {
  const record = asRecord(data);
  if (record === null) {
    return { name };
  }
  return { ...record, name };
}

/**
 * Converts a run's materialized definition graph into laid-out React Flow
 * elements. Unlike {@link workflowGraphToFlow}, runs have no separate trigger
 * layer — the `system/run-input` node is already part of the graph — so no
 * synthetic trigger edges are needed.
 *
 * For-each body steps with multiple instances are expanded into parallel cards
 * so the run view fans out visually. Fan-in continuations join under those
 * body instance cards (not directly under the for-each parent).
 */
export function runGraphToFlow(
  nodes: readonly WorkflowRunGraphNode[],
  edges: readonly WorkflowRunGraphEdge[]
): { nodes: WorkflowRunFlowNode[]; edges: Edge[] } {
  const expanded = expandRunGraphForFanOut(nodes, edges);

  const flowNodes: WorkflowRunFlowNode[] = expanded.cards.map((card) => {
    const baseData = mergeDisplayName(card.node.data, card.node.name);
    const nodeData =
      card.itemIndex !== null
        ? withForcedDisplayName(
            baseData,
            `${card.node.name} · ${card.itemIndex}`
          )
        : baseData;
    const viewData: WorkflowNodeViewData = {
      id: card.definitionNodeId,
      type: card.node.type,
      data: nodeData,
      inputShape: card.node.inputShape,
      defaultOutput: card.node.defaultOutput,
    };
    return {
      id: card.flowId,
      type: WORKFLOW_RUN_FLOW_NODE_TYPE,
      position: { x: 0, y: 0 },
      data: {
        node: viewData,
        status: card.status,
        definitionNodeId: card.definitionNodeId,
        instanceKey: card.instanceKey,
      },
    };
  });

  const flowEdges: Edge[] = expanded.edges.map((edge) => ({
    id: edge.id,
    source: edge.fromFlowId,
    target: edge.toFlowId,
    type: WORKFLOW_EDGE_TYPE,
  }));

  return layoutFlowGraph(flowNodes, flowEdges, () => ({
    width: WORKFLOW_FLOW_NODE_WIDTH,
    height: WORKFLOW_FLOW_NODE_HEIGHT,
  }));
}
